import { useState, useCallback } from 'react';
import { Sparkles, Loader2, Copy, Check, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const AI_TARGETS = ['chatgpt', 'claude', 'gemini', 'perplexity', 'copilot', 'cursor'];
const CATEGORIES = ['general', 'writing', 'coding', 'analysis', 'research', 'brainstorm', 'email', 'strategy', 'image', 'video', 'legal', 'medical', 'sales', 'marketing', 'design'];

export default function PICastPage() {
  const [outcome, setOutcome] = useState('');
  const [context, setContext] = useState('');
  const [targetAi, setTargetAi] = useState('chatgpt');
  const [category, setCategory] = useState('general');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleCast = useCallback(async () => {
    if (outcome.trim().length < 5 || loading) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/ai/cast`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome, target_ai: targetAi, category, context }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); }
      setResult(await res.json());
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [outcome, targetAi, category, context, loading]);

  const handleCopy = () => {
    if (result?.cast) {
      navigator.clipboard.writeText(result.cast);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSave = async () => {
    if (!result?.cast || saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/ai/my-casts`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cast: result.cast, title: outcome.slice(0, 60), category, target_ai: targetAi, source: 'pi_cast' }),
      });
      if (res.ok) toast.success('Cast saved to your library!');
      else throw new Error('Failed to save');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="cast-page">
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">PI Cast</h1>
        <p className="text-white/40 text-sm">State any outcome. Get a precision cast for any AI platform.</p>
      </div>

      {/* How to use */}
      <div className="bg-[#0E1830] border border-white/5 rounded-xl p-4">
        <details>
          <summary className="text-white/50 text-xs font-bold uppercase tracking-wider cursor-pointer hover:text-white/70">How to use PI Cast</summary>
          <div className="mt-3 text-white/35 text-xs space-y-1 leading-relaxed">
            <p>1. Describe your desired outcome in plain English (e.g., &ldquo;Generate a competitive analysis&rdquo;)</p>
            <p>2. Add optional context for more specific results</p>
            <p>3. Select the target AI platform and category</p>
            <p>4. Click &ldquo;Generate Cast&rdquo; to create a precision cast</p>
            <p>5. Copy to use immediately, or Save to your cast library</p>
          </div>
        </details>
      </div>

      {/* Samples */}
      <div className="flex flex-wrap gap-2">
        <span className="text-white/20 text-xs self-center">Try:</span>
        {[
          { label: 'Competitive analysis', outcome: 'Generate a competitive analysis of the top 5 CRM platforms', cat: 'research' },
          { label: 'Landing page copy', outcome: 'Write landing page copy that converts at 5%+ for a B2B SaaS product', cat: 'marketing' },
          { label: 'Code architecture', outcome: 'Design a microservices architecture for a real-time chat application', cat: 'coding' },
          { label: 'Legal review', outcome: 'Review a SaaS terms of service for compliance gaps', cat: 'legal' },
        ].map(s => (
          <button key={s.label} onClick={() => { setOutcome(s.outcome); setCategory(s.cat); setResult(null); }}
            className="px-3 py-1 text-xs rounded-full border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all">{s.label}</button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/30 font-bold block mb-2">What do you want to achieve?</label>
            <textarea value={outcome} onChange={e => setOutcome(e.target.value)}
              placeholder="e.g. Generate a competitive analysis of the top 5 CRM platforms..."
              className="w-full h-24 bg-white/[0.04] border border-white/[0.08] rounded-xl p-4 text-white/90 text-sm resize-none focus:border-[#7C3AED]/40 focus:outline-none placeholder:text-white/20"
              data-testid="cast-outcome" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/30 font-bold block mb-2">Additional context (optional)</label>
            <input value={context} onChange={e => setContext(e.target.value)}
              placeholder="e.g. for a SaaS startup, focus on pricing..."
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-white/90 text-xs focus:border-[#7C3AED]/40 focus:outline-none placeholder:text-white/20"
              data-testid="cast-context" />
          </div>
          <div className="space-y-2">
            <span className="text-[10px] uppercase tracking-widest text-white/30 font-bold">Target AI</span>
            <div className="flex flex-wrap gap-1.5">
              {AI_TARGETS.map(ai => (
                <button key={ai} onClick={() => setTargetAi(ai)}
                  className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-all capitalize ${targetAi === ai ? 'bg-[#7C3AED] text-white border-[#7C3AED]' : 'bg-white/[0.03] text-white/40 border-white/[0.08] hover:text-white/60'}`}>{ai}</button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <span className="text-[10px] uppercase tracking-widest text-white/30 font-bold">Category</span>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map(c => (
                <button key={c} onClick={() => setCategory(c)}
                  className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-all capitalize ${category === c ? 'bg-[#06B6D4] text-white border-[#06B6D4]' : 'bg-white/[0.03] text-white/40 border-white/[0.08] hover:text-white/60'}`}>{c}</button>
              ))}
            </div>
          </div>
          <Button onClick={handleCast} disabled={loading || outcome.trim().length < 5}
            className="w-full shimmer-btn text-white rounded-full font-space font-bold border-0" data-testid="cast-btn">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Cast</>}
          </Button>
        </div>

        {/* Result */}
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
          {result ? (
            <div className="space-y-4" data-testid="cast-result">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-[#F59E0B] uppercase tracking-wider">Precision Cast</span>
                  <p className="text-white/30 text-[10px] mt-0.5">for {result.target_ai} &middot; {result.token_count} tokens &middot; {result.estimated_cost}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleCopy} className="text-xs text-[#7C3AED] hover:underline flex items-center gap-1" data-testid="copy-cast">
                    {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                  <button onClick={handleSave} disabled={saving} className="text-xs text-emerald-400 hover:underline flex items-center gap-1" data-testid="save-cast">
                    <Save className="w-3 h-3" /> {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 max-h-[350px] overflow-y-auto">
                <pre className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap font-mono m-0" data-testid="cast-text">{result.cast}</pre>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <Sparkles className="w-10 h-10 text-white/10 mb-3" />
              <p className="text-white/30 text-sm">State your outcome to generate a cast</p>
              <p className="text-white/15 text-xs mt-1">Works for any AI platform, any category</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
